// Pestanya "Control Horari" de Gestió d'un Empleat (CURRENT_TASK.md §30-32,
// fase 4): per cada dia del mes seleccionat mostra l'Horari previst
// (reaprofitant chResoldreRangEmpleat, ja usat a la pestanya "Calendari") i
// el fitxatge real (FullDiari + Events, ja usat pel portal mòbil), amb el
// còmput de temps efectiu vs. previst.
//
// Consulta (fila oberta): taula només lectura (GPS inclòs). Corregir és
// un canvi puntual i immediat (ControlHorariCorreccio.php), sense obrir
// l'esborrany. Afegir: taller en memòria (afegir / corregir / esborrar,
// cadascun amb motiu); Sincronitzar aplica tot el dia
// (ControlHorariSincronitzarDia.php). El dia d'avui es pot sincronitzar
// sense sortida. Un dia passat, no. Cancel·lar desfa l'esborrany.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate, formatTime } from "../../utils/Utilitats";
import { API_URL } from "../../utils/config";
import { NOMS_MESOS, formatTramsDia } from "./HorariEditor";
import "./controlhorariempleat.css";

const TIPUS_EVENT_OPCIONS = [
  { valor: "ENTRADA", nom: "Entrada" },
  { valor: "INICI_PAUSA", nom: "Inici de pausa" },
  { valor: "FI_PAUSA", nom: "Fi de pausa" },
  { valor: "SORTIDA", nom: "Sortida" },
];

const ETIQUETA_ORIGEN = { MOBIL: "Mòbil", ADMIN: "Correcció manual", SISTEMA: "Sistema" };

const ETIQUETA_GPS = {
  PERMIS_DENEGAT: "Permís d'ubicació denegat",
  PERMIS_BLOQUEJAT: "Ubicació bloquejada al navegador o dispositiu",
};

const TRANSICIONS = {
  "": ["ENTRADA"],
  ENTRADA: ["INICI_PAUSA", "SORTIDA"],
  INICI_PAUSA: ["FI_PAUSA"],
  FI_PAUSA: ["INICI_PAUSA", "SORTIDA"],
  SORTIDA: [],
};

function formatGpsEvent(ev) {
  if (!ev || !ev.EstatGPS) return "—";
  if (ev.EstatGPS === "OK") {
    const lat = ev.Latitud != null && ev.Latitud !== "" ? Number(ev.Latitud) : null;
    const lng = ev.Longitud != null && ev.Longitud !== "" ? Number(ev.Longitud) : null;
    const prec = ev.PrecisioMetres != null && ev.PrecisioMetres !== "" ? Number(ev.PrecisioMetres) : null;
    const posicio =
      lat != null && !Number.isNaN(lat) && lng != null && !Number.isNaN(lng)
        ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
        : "—";
    const precisio = prec != null && !Number.isNaN(prec) ? `${Math.round(prec)} m` : "—";
    return `Posició: ${posicio}   Precisió: ${precisio}`;
  }
  return ETIQUETA_GPS[ev.EstatGPS] || ev.EstatGPS;
}

function formatDataISOLocal(data) {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
}

export function formatMinuts(minuts) {
  if (minuts === null || minuts === undefined) return "—";
  const signe = minuts < 0 ? "-" : "";
  const abs = Math.abs(minuts);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${signe}${h}h ${String(m).padStart(2, "0")}m`;
}

function centrarFilaAlContenidor(fila) {
  const contenidor = fila?.closest(".Dashboard-table-container");
  if (!contenidor || contenidor.clientHeight <= 0) return false;
  const capcalera = contenidor.querySelector("thead");
  const altCapcalera = capcalera ? capcalera.getBoundingClientRect().height : 0;
  const contenidorRect = contenidor.getBoundingClientRect();
  const filaRect = fila.getBoundingClientRect();
  const filaDins = filaRect.top - contenidorRect.top + contenidor.scrollTop;
  const zonaVisible = Math.max(0, contenidor.clientHeight - altCapcalera);
  const objectiu = filaDins - altCapcalera - (zonaVisible - filaRect.height) / 2;
  const maxim = Math.max(0, contenidor.scrollHeight - contenidor.clientHeight);
  contenidor.scrollTop = Math.max(0, Math.min(maxim, objectiu));
  return true;
}

function dataHoraAInputLocal(dataHoraStr) {
  if (!dataHoraStr) return "";
  return dataHoraStr.replace(" ", "T").slice(0, 16);
}

function inputLocalADataHora(valorInput) {
  if (!valorInput) return "";
  return `${valorInput.replace("T", " ")}:00`;
}

function formatHoraEvent(ev, dataDia) {
  const dh = ev.DataHoraEvent || "";
  if (String(dh).slice(0, 10) !== dataDia) {
    return `${formatDate(dh)} ${formatTime(dh)}`;
  }
  return formatTime(dh);
}

function clonarEvents(events) {
  return (events || []).map((ev) => ({
    clau: String(ev.idRegistreJornadaEvent || ev.clau),
    idRegistreJornadaEvent: ev.idRegistreJornadaEvent || null,
    TipusEvent: ev.TipusEvent,
    DataHoraEvent: ev.DataHoraEvent,
    idTipusPausa: ev.TipusPausaEmpleats_idTipusPausaEmpleats || ev.idTipusPausa || "",
    TipusPausaNom: ev.TipusPausaNom || "",
    OrigenEvent: ev.OrigenEvent,
    EsOffline: ev.EsOffline,
    EstatGPS: ev.EstatGPS,
    Latitud: ev.Latitud,
    Longitud: ev.Longitud,
    PrecisioMetres: ev.PrecisioMetres,
    motiuCorreccio: ev.motiuCorreccio || "",
    notes: ev.notes || ev.Notes || "",
  }));
}

function fingerprintEvents(events) {
  return (events || [])
    .map((ev) => [
      ev.idRegistreJornadaEvent || "nou",
      ev.TipusEvent,
      dataHoraAInputLocal(ev.DataHoraEvent),
      String(ev.idTipusPausa || ""),
      ev.motiuCorreccio || "",
    ].join("|"))
    .join(";");
}

function ordenarEvents(events) {
  return [...events].sort((a, b) => {
    const ha = dataHoraAInputLocal(a.DataHoraEvent);
    const hb = dataHoraAInputLocal(b.DataHoraEvent);
    if (ha !== hb) return ha < hb ? -1 : 1;
    return String(a.clau).localeCompare(String(b.clau));
  });
}

function validarIntegritatEsborrany(events, dataJornada, dataAvuiISO, exigeixTancada) {
  const ordenats = ordenarEvents(events);
  if (ordenats.length === 0) return null;
  let ultim = "";
  let horaEntrada = null;
  let horaSortida = null;
  for (const ev of ordenats) {
    if (!ev.DataHoraEvent) return "Tots els esdeveniments han de tenir data i hora.";
    if (ev.TipusEvent === "INICI_PAUSA" && !ev.idTipusPausa) {
      return "Cal indicar el Tipus de pausa a cada Inici de pausa.";
    }
    const permeses = TRANSICIONS[ultim] || TRANSICIONS[""];
    if (!permeses.includes(ev.TipusEvent)) {
      return `No es pot registrar '${ev.TipusEvent}' després de '${ultim || "l'inici"}'.`;
    }
    ultim = ev.TipusEvent;
    if (ev.TipusEvent === "ENTRADA" && !horaEntrada) horaEntrada = ev.DataHoraEvent;
    if (ev.TipusEvent === "SORTIDA") horaSortida = ev.DataHoraEvent;
  }
  if (!horaEntrada) return "Cal una Entrada.";
  if (exigeixTancada && !horaSortida) return "Si hi ha events, cal Entrada i Sortida.";
  if (dataJornada < dataAvuiISO && !horaSortida) {
    return "Un dia ja passat ha d'acabar amb Sortida.";
  }
  if (horaEntrada && horaSortida) {
    const tInici = new Date(String(horaEntrada).replace(" ", "T")).getTime();
    const tFinal = new Date(String(horaSortida).replace(" ", "T")).getTime();
    if (Number.isNaN(tInici) || Number.isNaN(tFinal)) return "Data o hora d'esdeveniment no vàlida.";
    if (tFinal < tInici) return "La Sortida no pot ser anterior a l'Entrada.";
    if (tFinal - tInici > 24 * 3600 * 1000) {
      return "La Sortida no pot ser més de 24 hores després de l'Entrada.";
    }
  }
  return null;
}

function ControlHorariEmpleat({ idEmpleat, idEmpresa }) {
  const { rolUsuari } = useUserContext();
  const potCorregir = tincPermis(rolUsuari, "MODIFICAR");

  const avui = useMemo(() => new Date(), []);
  const dataAvuiISO = useMemo(() => formatDataISOLocal(avui), [avui]);
  const [any, setAny] = useState(avui.getFullYear());
  const [mes, setMes] = useState(avui.getMonth());

  const [dies, setDies] = useState([]);
  const [tipusPausa, setTipusPausa] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [diaObert, setDiaObert] = useState(dataAvuiISO);
  const filaAvuiRef = useRef(null);
  const empleatEnfocatRef = useRef(null);
  const calFerScrollAvuiRef = useRef(false);
  const clauNouRef = useRef(1);

  const [modeEdicio, setModeEdicio] = useState(false);
  const [esborrany, setEsborrany] = useState([]);
  const [empremtaOriginal, setEmpremtaOriginal] = useState("");
  const [formCorreccio, setFormCorreccio] = useState(null);
  const [eventsRetirats, setEventsRetirats] = useState([]);
  const [enviant, setEnviant] = useState(false);
  const [errorForm, setErrorForm] = useState("");
  const [pendent, setPendent] = useState(null);

  const esbrut = modeEdicio && (
    fingerprintEvents(esborrany) !== empremtaOriginal || eventsRetirats.length > 0
  );

  const dataInici = useMemo(() => `${any}-${String(mes + 1).padStart(2, "0")}-01`, [any, mes]);
  const dataFinal = useMemo(() => {
    const ultimDia = new Date(any, mes + 1, 0).getDate();
    return `${any}-${String(mes + 1).padStart(2, "0")}-${String(ultimDia).padStart(2, "0")}`;
  }, [any, mes]);

  const carregarDades = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpleat, idEmpresa, dataInici, dataFinal });
      const res = await fetchAutenticat(`${API_URL}/ControlHorariEmpleat.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        const diesNous = data.dies || [];
        const canviEmpleat = empleatEnfocatRef.current !== idEmpleat;
        empleatEnfocatRef.current = idEmpleat;
        setDies(diesNous);
        setTipusPausa(data.tipusPausa || []);
        if (canviEmpleat && diesNous.some((d) => d.Data === dataAvuiISO)) {
          setDiaObert(dataAvuiISO);
          calFerScrollAvuiRef.current = true;
        }
      } else {
        setError(data.message || "No s'ha pogut carregar el Control Horari.");
        setDies([]);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpleat, idEmpresa, dataInici, dataFinal, dataAvuiISO]);

  useEffect(() => {
    carregarDades();
  }, [carregarDades]);

  useEffect(() => {
    if (loading || !calFerScrollAvuiRef.current || diaObert !== dataAvuiISO) return;
    let cancel = false;
    let intents = 0;
    let marcador = 0;
    const intentar = () => {
      if (cancel) return;
      intents += 1;
      const centrat = centrarFilaAlContenidor(filaAvuiRef.current);
      if (!centrat && intents < 12) {
        marcador = requestAnimationFrame(intentar);
        return;
      }
      calFerScrollAvuiRef.current = false;
    };
    marcador = requestAnimationFrame(intentar);
    return () => {
      cancel = true;
      cancelAnimationFrame(marcador);
    };
  }, [loading, diaObert, dataAvuiISO, dies]);

  const tancarFormulari = useCallback(() => {
    setFormCorreccio(null);
    setErrorForm("");
  }, []);

  const sortirEdicio = useCallback(() => {
    setModeEdicio(false);
    setEsborrany([]);
    setEmpremtaOriginal("");
    setEventsRetirats([]);
    setFormCorreccio(null);
    setErrorForm("");
    setPendent(null);
  }, []);

  const aplicarCanviMes = (delta) => {
    let novaMes = mes + delta;
    let novaAny = any;
    if (novaMes < 0) {
      novaMes = 11;
      novaAny -= 1;
    } else if (novaMes > 11) {
      novaMes = 0;
      novaAny += 1;
    }
    setMes(novaMes);
    setAny(novaAny);
    setDiaObert(null);
    sortirEdicio();
  };

  const intentarCanviarMes = (delta) => {
    if (esbrut) {
      setPendent({ tipus: "mes", delta });
      return;
    }
    aplicarCanviMes(delta);
  };

  const obrirDiaDirecte = (data) => {
    sortirEdicio();
    setDiaObert(data);
  };

  const tancarDiaDirecte = () => {
    sortirEdicio();
    setDiaObert(null);
  };

  const intentarObrirDia = (data) => {
    if (diaObert === data) {
      if (esbrut) {
        setPendent({ tipus: "tancar" });
        return;
      }
      tancarDiaDirecte();
      return;
    }
    if (esbrut) {
      setPendent({ tipus: "obrir", data });
      return;
    }
    obrirDiaDirecte(data);
  };

  const entrarModificar = (dia) => {
    const clon = clonarEvents(dia?.events);
    setEsborrany(clon);
    setEmpremtaOriginal(fingerprintEvents(clon));
    setEventsRetirats([]);
    setModeEdicio(true);
    setErrorForm("");
    setPendent(null);
    setFormCorreccio(null);
  };

  const cancelarEdicio = () => {
    sortirEdicio();
  };

  const descartarCanvis = () => {
    const accio = pendent;
    sortirEdicio();
    if (!accio) return;
    if (accio.tipus === "tancar") tancarDiaDirecte();
    else if (accio.tipus === "obrir") obrirDiaDirecte(accio.data);
    else if (accio.tipus === "mes") aplicarCanviMes(accio.delta);
  };

  const obrirAfegirEvent = (data) => {
    const ordenats = ordenarEvents(esborrany);
    const ultim = ordenats[ordenats.length - 1];
    let tipus = "ENTRADA";
    if (ultim?.TipusEvent === "INICI_PAUSA") tipus = "FI_PAUSA";
    else if (ultim) tipus = "INICI_PAUSA";
    setFormCorreccio({
      mode: "esborrany",
      clau: null,
      idEventOriginal: null,
      tipusEvent: tipus,
      dataHoraEvent: dataHoraAInputLocal(`${data} 08:00:00`),
      idTipusPausa: "",
      notes: "",
      motiuCorreccio: "",
    });
    setErrorForm("");
  };

  const obrirCorregirEsborrany = (event) => {
    setFormCorreccio({
      mode: "esborrany",
      clau: event.clau || String(event.idRegistreJornadaEvent),
      idEventOriginal: event.idRegistreJornadaEvent || null,
      tipusEvent: event.TipusEvent,
      dataHoraEvent: dataHoraAInputLocal(event.DataHoraEvent),
      idTipusPausa: event.idTipusPausa || event.TipusPausaEmpleats_idTipusPausaEmpleats || "",
      notes: event.notes || event.Notes || "",
      motiuCorreccio: event.motiuCorreccio || "",
    });
    setErrorForm("");
  };

  const obrirCorregirConsulta = (event) => {
    setFormCorreccio({
      mode: "consulta",
      clau: String(event.idRegistreJornadaEvent),
      idEventOriginal: event.idRegistreJornadaEvent,
      tipusEvent: event.TipusEvent,
      dataHoraEvent: dataHoraAInputLocal(event.DataHoraEvent),
      idTipusPausa: event.TipusPausaEmpleats_idTipusPausaEmpleats || event.idTipusPausa || "",
      notes: event.Notes || event.notes || "",
      motiuCorreccio: "",
    });
    setErrorForm("");
  };

  const obrirEsborrarEsborrany = (event) => {
    setFormCorreccio({
      mode: "esborrar",
      clau: event.clau || String(event.idRegistreJornadaEvent),
      idEventOriginal: event.idRegistreJornadaEvent || null,
      tipusEvent: event.TipusEvent,
      dataHoraEvent: dataHoraAInputLocal(event.DataHoraEvent),
      idTipusPausa: "",
      notes: "",
      motiuCorreccio: "",
    });
    setErrorForm("");
  };

  const acceptarFormulariEsborrany = () => {
    if (!formCorreccio) return;
    if (formCorreccio.mode === "esborrar") {
      if (!formCorreccio.motiuCorreccio.trim()) {
        setErrorForm("Indica el motiu de l'esborrat.");
        return;
      }
      const idOrig = formCorreccio.idEventOriginal ? Number(formCorreccio.idEventOriginal) : null;
      if (idOrig) {
        setEventsRetirats((prev) => [
          ...prev.filter((r) => Number(r.idRegistreJornadaEvent) !== idOrig),
          { idRegistreJornadaEvent: idOrig, motiuCorreccio: formCorreccio.motiuCorreccio.trim() },
        ]);
      }
      setEsborrany((prev) => prev.filter((ev) => ev.clau !== formCorreccio.clau));
      tancarFormulari();
      return;
    }
    if (!formCorreccio.dataHoraEvent) {
      setErrorForm("Indica la data i hora de l'esdeveniment.");
      return;
    }
    if (formCorreccio.tipusEvent === "INICI_PAUSA" && !formCorreccio.idTipusPausa) {
      setErrorForm("Selecciona el Tipus de pausa.");
      return;
    }
    if (!formCorreccio.motiuCorreccio.trim()) {
      setErrorForm("Indica el motiu de la correcció.");
      return;
    }
    const tp = tipusPausa.find((t) => String(t.idTipusPausaEmpleats) === String(formCorreccio.idTipusPausa));
    const dades = {
      TipusEvent: formCorreccio.tipusEvent,
      DataHoraEvent: inputLocalADataHora(formCorreccio.dataHoraEvent),
      idTipusPausa: formCorreccio.tipusEvent === "INICI_PAUSA" ? formCorreccio.idTipusPausa : "",
      TipusPausaNom: formCorreccio.tipusEvent === "INICI_PAUSA" ? (tp?.Nom || "") : "",
      motiuCorreccio: formCorreccio.motiuCorreccio.trim(),
      notes: formCorreccio.notes.trim(),
      OrigenEvent: "ADMIN",
    };
    if (formCorreccio.clau) {
      setEsborrany((prev) =>
        prev.map((ev) => (ev.clau === formCorreccio.clau ? { ...ev, ...dades } : ev))
      );
    } else {
      clauNouRef.current += 1;
      setEsborrany((prev) => [
        ...prev,
        {
          clau: `nou-${clauNouRef.current}`,
          idRegistreJornadaEvent: null,
          EsOffline: 0,
          EstatGPS: "NO_DISPONIBLE",
          ...dades,
        },
      ]);
    }
    tancarFormulari();
  };

  const handleDesarCorreccioConsulta = async (eventsDia) => {
    if (!formCorreccio || formCorreccio.mode !== "consulta") return;
    if (!formCorreccio.dataHoraEvent) {
      setErrorForm("Indica la data i hora de l'esdeveniment.");
      return;
    }
    if (formCorreccio.tipusEvent === "INICI_PAUSA" && !formCorreccio.idTipusPausa) {
      setErrorForm("Selecciona el Tipus de pausa.");
      return;
    }
    if (!formCorreccio.motiuCorreccio.trim()) {
      setErrorForm("Indica el motiu de la correcció.");
      return;
    }
    const tp = tipusPausa.find((t) => String(t.idTipusPausaEmpleats) === String(formCorreccio.idTipusPausa));
    const simulats = (eventsDia || [])
      .filter((ev) => Number(ev.idRegistreJornadaEvent) !== Number(formCorreccio.idEventOriginal))
      .map((ev) => ({
        TipusEvent: ev.TipusEvent,
        DataHoraEvent: ev.DataHoraEvent,
        idTipusPausa: ev.TipusPausaEmpleats_idTipusPausaEmpleats || ev.idTipusPausa || "",
      }));
    simulats.push({
      TipusEvent: formCorreccio.tipusEvent,
      DataHoraEvent: inputLocalADataHora(formCorreccio.dataHoraEvent),
      idTipusPausa: formCorreccio.tipusEvent === "INICI_PAUSA" ? formCorreccio.idTipusPausa : "",
      TipusPausaNom: formCorreccio.tipusEvent === "INICI_PAUSA" ? (tp?.Nom || "") : "",
    });
    const errorInt = validarIntegritatEsborrany(simulats, diaObert, dataAvuiISO, false);
    if (errorInt) {
      setErrorForm(errorInt);
      return;
    }
    setEnviant(true);
    setErrorForm("");
    try {
      const res = await fetchAutenticat(`${API_URL}/ControlHorariCorreccio.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idEmpleat,
          data: diaObert,
          idEventOriginal: formCorreccio.idEventOriginal,
          tipusEvent: formCorreccio.tipusEvent,
          dataHoraEvent: inputLocalADataHora(formCorreccio.dataHoraEvent),
          idTipusPausa: formCorreccio.tipusEvent === "INICI_PAUSA" ? formCorreccio.idTipusPausa || null : null,
          motiuCorreccio: formCorreccio.motiuCorreccio.trim(),
          notes: formCorreccio.notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        tancarFormulari();
        await carregarDades();
      } else {
        setErrorForm(data.message || "No s'ha pogut corregir l'esdeveniment.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorForm("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const handleSincronitzar = async () => {
    if (formCorreccio) {
      setErrorForm("Tanca el formulari abans de sincronitzar.");
      return;
    }
    const errorSeq = validarIntegritatEsborrany(esborrany, diaObert, dataAvuiISO, false);
    if (errorSeq) {
      setErrorForm(errorSeq);
      return;
    }
    const ordenats = ordenarEvents(esborrany);
    const originalPerId = {};
    (dies.find((d) => d.Data === diaObert)?.events || []).forEach((ev) => {
      originalPerId[ev.idRegistreJornadaEvent] = ev;
    });
    for (const ev of ordenats) {
      const orig = ev.idRegistreJornadaEvent ? originalPerId[ev.idRegistreJornadaEvent] : null;
      const esNou = !ev.idRegistreJornadaEvent;
      const canviat = orig && (
        orig.TipusEvent !== ev.TipusEvent
        || dataHoraAInputLocal(orig.DataHoraEvent) !== dataHoraAInputLocal(ev.DataHoraEvent)
        || String(orig.TipusPausaEmpleats_idTipusPausaEmpleats || "") !== String(ev.idTipusPausa || "")
      );
      if ((esNou || canviat) && !String(ev.motiuCorreccio || "").trim()) {
        setErrorForm("Cada esdeveniment nou o corregit ha de tenir el seu motiu.");
        return;
      }
    }
    for (const r of eventsRetirats) {
      if (!String(r.motiuCorreccio || "").trim()) {
        setErrorForm("Cada esdeveniment esborrat ha de tenir el seu motiu.");
        return;
      }
    }
    setEnviant(true);
    setErrorForm("");
    try {
      const res = await fetchAutenticat(`${API_URL}/ControlHorariSincronitzarDia.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idEmpleat,
          data: diaObert,
          events: ordenats.map((ev) => ({
            idRegistreJornadaEvent: ev.idRegistreJornadaEvent || null,
            tipusEvent: ev.TipusEvent,
            dataHoraEvent: ev.DataHoraEvent,
            idTipusPausa: ev.TipusEvent === "INICI_PAUSA" ? ev.idTipusPausa || null : null,
            motiuCorreccio: ev.motiuCorreccio || "",
            notes: ev.notes || null,
          })),
          eventsRetirats: eventsRetirats.map((r) => ({
            idRegistreJornadaEvent: r.idRegistreJornadaEvent,
            motiuCorreccio: r.motiuCorreccio,
          })),
        }),
      });
      const data = await res.json();
      if (data.success) {
        const accio = pendent;
        sortirEdicio();
        await carregarDades();
        if (accio?.tipus === "tancar") tancarDiaDirecte();
        else if (accio?.tipus === "obrir") obrirDiaDirecte(accio.data);
        else if (accio?.tipus === "mes") aplicarCanviMes(accio.delta);
      } else {
        setErrorForm(data.message || "No s'ha pogut sincronitzar la jornada.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorForm("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  const colorMotiuSenseHorari = (dia) => {
    if (dia.ForaVigencia) return "#9e9e9e";
    if (dia.Baixa) return dia.Baixa.TipusColor || "#999999";
    if (dia.AbsenciaCompleta) return dia.AbsenciaCompleta.TipusColor || "#999999";
    if (dia.TipusFestiu === "EMPRESA") return "#0099CC";
    if (dia.Festiu) return "#607d8b";
    return "#999999";
  };

  const estatDia = (dia) => {
    const xips = [];
    if (dia.MotiuSenseHorari) {
      xips.push({ text: dia.MotiuSenseHorari, color: colorMotiuSenseHorari(dia) });
    } else if (dia.TramsPrevists.length === 0) {
      xips.push({ text: "No laborable", color: "#b0bec5" });
    }
    dia.AbsenciesParcials.forEach((a) => xips.push({ text: `${a.TipusNom} (parcial)`, color: a.TipusColor || "#999999" }));
    return xips;
  };

  const renderTaulaEvents = (llista, dataDia, { esEdicio }) => {
    if (!llista || llista.length === 0) {
      return <p>No hi ha cap esdeveniment de fitxatge registrat aquest dia.</p>;
    }
    return (
      <table className="Dashboard-table ch-emp-events-taula">
        <thead>
          <tr>
            <th>Hora</th>
            <th>Esdeveniment</th>
            <th>Pausa</th>
            <th>Origen</th>
            <th>GPS</th>
            {potCorregir && <th></th>}
          </tr>
        </thead>
        <tbody>
          {llista.map((ev) => (
            <tr key={ev.clau || ev.idRegistreJornadaEvent}>
              <td>{formatHoraEvent(ev, dataDia)}</td>
              <td>{TIPUS_EVENT_OPCIONS.find((t) => t.valor === ev.TipusEvent)?.nom || ev.TipusEvent}</td>
              <td>{ev.TipusPausaNom || "—"}</td>
              <td>
                {ev.idRegistreJornadaEvent
                  ? (ETIQUETA_ORIGEN[ev.OrigenEvent] || ev.OrigenEvent)
                  : "Esborrany"}
                {Number(ev.EsOffline) === 1 && " (offline)"}
              </td>
              <td>{formatGpsEvent(ev)}</td>
              {potCorregir && (
                <td>
                  <div className="ch-emp-accions-event">
                    <button
                      type="button"
                      className="Dashboard-btn ch-emp-btn-petit"
                      onClick={() => {
                        if (esEdicio) obrirCorregirEsborrany(ev);
                        else obrirCorregirConsulta(ev);
                      }}
                    >
                      Corregir
                    </button>
                    {esEdicio && (
                      <button
                        type="button"
                        className="Dashboard-btn ch-emp-btn-petit ch-emp-btn-esborrar"
                        onClick={() => obrirEsborrarEsborrany(ev)}
                      >
                        Esborrar
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const titolFormulari = () => {
    if (!formCorreccio) return "";
    if (formCorreccio.mode === "esborrar") return "Esborrar esdeveniment";
    if (formCorreccio.mode === "consulta") return "Corregir esdeveniment";
    return formCorreccio.clau ? "Corregir esdeveniment" : "Afegir esdeveniment";
  };

  const renderFormulariEvent = (eventsDia) => {
    if (!formCorreccio) return null;
    const esEsborrar = formCorreccio.mode === "esborrar";
    const esConsulta = formCorreccio.mode === "consulta";
    return (
      <div className="Dashboard-form-section ch-emp-form">
        <h4>{titolFormulari()}</h4>
        {!esEsborrar && (
          <div className="Dashboard-form-row">
            <label>
              Tipus d'esdeveniment:
              <select
                value={formCorreccio.tipusEvent}
                onChange={(e) => setFormCorreccio((p) => ({ ...p, tipusEvent: e.target.value }))}
              >
                {TIPUS_EVENT_OPCIONS.map((t) => (
                  <option key={t.valor} value={t.valor}>{t.nom}</option>
                ))}
              </select>
            </label>
            <label>
              Data i hora:
              <input
                type="datetime-local"
                value={formCorreccio.dataHoraEvent}
                onChange={(e) => setFormCorreccio((p) => ({ ...p, dataHoraEvent: e.target.value }))}
              />
            </label>
            {formCorreccio.tipusEvent === "INICI_PAUSA" && (
              <label>
                Tipus de pausa:
                <select
                  value={formCorreccio.idTipusPausa}
                  onChange={(e) => setFormCorreccio((p) => ({ ...p, idTipusPausa: e.target.value }))}
                >
                  <option value="">Selecciona...</option>
                  {tipusPausa.map((tp) => (
                    <option key={tp.idTipusPausaEmpleats} value={tp.idTipusPausaEmpleats}>{tp.Nom}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        <div className="Dashboard-form-row">
          <label>
            {esEsborrar ? "*Motiu de l'esborrat:" : "*Motiu de la correcció:"}
            <textarea
              value={formCorreccio.motiuCorreccio}
              onChange={(e) => setFormCorreccio((p) => ({ ...p, motiuCorreccio: e.target.value }))}
              maxLength="255"
            />
          </label>
        </div>
        {!esEsborrar && (
          <div className="Dashboard-form-row">
            <label>
              Notes (opcional):
              <textarea
                value={formCorreccio.notes}
                onChange={(e) => setFormCorreccio((p) => ({ ...p, notes: e.target.value }))}
                maxLength="255"
              />
            </label>
          </div>
        )}
        <div className="Dashboard-button-group">
          <button
            type="button"
            className="Dashboard-btn Dashboard-btn-acceptar"
            onClick={() => {
              if (esConsulta) handleDesarCorreccioConsulta(eventsDia);
              else acceptarFormulariEsborrany();
            }}
            disabled={enviant}
          >
            Acceptar
          </button>
          <button
            type="button"
            className="Dashboard-btn Dashboard-btn-cancelar"
            onClick={tancarFormulari}
            disabled={enviant}
          >
            Cancel·lar
          </button>
        </div>
      </div>
    );
  };

  if (!idEmpleat || !idEmpresa) {
    return <p>Falten dades (Empleat o Empresa) per carregar el Control Horari.</p>;
  }

  return (
    <div className="ch-emp">
      <div className="ch-emp-nav">
        <button type="button" className="Dashboard-btn" onClick={() => intentarCanviarMes(-1)}>‹</button>
        <span className="ch-emp-titol-mes">{NOMS_MESOS[mes]} {any}</span>
        <button type="button" className="Dashboard-btn" onClick={() => intentarCanviarMes(1)}>›</button>
      </div>

      {loading && <p>Carregant Control Horari...</p>}
      {error && <p className="ch-emp-error">{error}</p>}

      {!loading && !error && (
        <div className="Dashboard-table-container">
          <table className="Dashboard-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Estat</th>
                <th>Horari efectiu</th>
                <th>Entrada</th>
                <th>Sortida</th>
                <th>Previst</th>
                <th>Efectiu</th>
                <th>Desviació</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dies.map((dia) => {
                const xips = estatDia(dia);
                const desviacio = dia.comput.desviacioMinuts;
                const editantAquestDia = modeEdicio && diaObert === dia.Data;
                return (
                  <React.Fragment key={dia.Data}>
                    <tr
                      ref={dia.Data === dataAvuiISO ? filaAvuiRef : null}
                      className={`${dia.Data === dataAvuiISO ? "ch-emp-avui" : ""} ${diaObert === dia.Data ? "selected-row" : ""}`.trim()}
                      style={{ cursor: "pointer" }}
                      onClick={() => intentarObrirDia(dia.Data)}
                    >
                      <td>
                        {formatDate(dia.Data)}
                        {dia.Data === dataAvuiISO ? <span className="ch-emp-avui-marca">Avui</span> : null}
                      </td>
                      <td>
                        {xips.length > 0 ? (
                          xips.map((x, i) => (
                            <span key={i} className="ch-emp-xip" style={{ backgroundColor: x.color }}>
                              {x.text}
                            </span>
                          ))
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {dia.TramsEfectius.length > 0
                          ? formatTramsDia(dia.TramsEfectius)
                          : (dia.MotiuSenseHorari ? `Sense horari · ${dia.MotiuSenseHorari}` : "—")}
                      </td>
                      <td>{dia.fullDiari?.HoraEntrada ? formatTime(dia.fullDiari.HoraEntrada) : "—"}</td>
                      <td>{dia.fullDiari?.HoraSortida ? formatTime(dia.fullDiari.HoraSortida) : (dia.fullDiari?.HoraEntrada ? "En curs" : "—")}</td>
                      <td>{formatMinuts(dia.comput.previstMinuts)}</td>
                      <td>{dia.comput.tancada ? formatMinuts(dia.comput.efectiuMinuts) : "—"}</td>
                      <td className={desviacio > 0 ? "ch-emp-desviacio-pos" : desviacio < 0 ? "ch-emp-desviacio-neg" : ""}>
                        {dia.comput.tancada ? formatMinuts(desviacio) : "—"}
                      </td>
                      <td>{diaObert === dia.Data ? "▲" : "▼"}</td>
                    </tr>

                    {diaObert === dia.Data && (
                      <tr>
                        <td colSpan={9}>
                          <div className="ch-emp-detall" onClick={(e) => e.stopPropagation()}>
                            {editantAquestDia && esbrut && (
                              <p className="ch-emp-esborrany-avis">
                                Canvis en esborrany: encara no s'han aplicat al diari.
                                {dia.Data < dataAvuiISO
                                  ? " Un dia passat s'ha de sincronitzar amb la sortida."
                                  : " Avui es pot sincronitzar sense sortida: la jornada pot seguir oberta."}
                              </p>
                            )}

                            {pendent && editantAquestDia && (
                              <div className="ch-emp-confirmar">
                                <p>Hi ha canvis sense sincronitzar.</p>
                                <div className="Dashboard-button-group">
                                  <button type="button" className="Dashboard-btn Dashboard-btn-acceptar" onClick={handleSincronitzar} disabled={enviant}>
                                    Sincronitzar
                                  </button>
                                  <button type="button" className="Dashboard-btn" onClick={descartarCanvis} disabled={enviant}>
                                    Descarta
                                  </button>
                                  <button type="button" className="Dashboard-btn Dashboard-btn-cancelar" onClick={() => setPendent(null)} disabled={enviant}>
                                    Continuar editant
                                  </button>
                                </div>
                              </div>
                            )}

                            {renderTaulaEvents(
                              editantAquestDia ? ordenarEvents(esborrany) : dia.events,
                              dia.Data,
                              { esEdicio: editantAquestDia }
                            )}

                            {potCorregir && !editantAquestDia && (
                              <>
                                {formCorreccio?.mode === "consulta" && renderFormulariEvent(dia.events)}
                                {errorForm && <p className="ch-emp-error">{errorForm}</p>}
                                <div className="Dashboard-button-group">
                                  <button
                                    type="button"
                                    className="Dashboard-btn Dashboard-btn-acceptar"
                                    onClick={() => entrarModificar(dia)}
                                  >
                                    Afegir
                                  </button>
                                </div>
                              </>
                            )}

                            {potCorregir && editantAquestDia && (
                              <>
                                {formCorreccio && formCorreccio.mode !== "consulta" && renderFormulariEvent(dia.events)}
                                {errorForm && <p className="ch-emp-error">{errorForm}</p>}
                                <div className="Dashboard-button-group">
                                  <button
                                    type="button"
                                    className="Dashboard-btn"
                                    onClick={() => obrirAfegirEvent(dia.Data)}
                                    disabled={enviant}
                                  >
                                    Afegir esdeveniment
                                  </button>
                                  <button
                                    type="button"
                                    className="Dashboard-btn Dashboard-btn-acceptar"
                                    onClick={handleSincronitzar}
                                    disabled={enviant || !esbrut}
                                  >
                                    Sincronitzar
                                  </button>
                                  <button
                                    type="button"
                                    className="Dashboard-btn Dashboard-btn-cancelar"
                                    onClick={cancelarEdicio}
                                    disabled={enviant}
                                  >
                                    Cancel·lar
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ControlHorariEmpleat;
